import { Code, ConnectError } from '@bufbuild/connect';
import { Timestamp } from '@bufbuild/protobuf';
import { SortOrder } from 'antd/es/table/interface';
import { useCallback, useRef } from 'react';

import { AdminOrganizationListStreamSortBy } from 'protocol/api/core/svc_adminka_pb';

import api from '@/shared/api/api';
import { useAppDispatch } from '@/shared/hooks';

import { ORGANIZATION_LIST_PAGE_SIZE, OrganizationListFilter } from '../config';
import * as organizationsModel from '../model';

type OpenOrganizationListStream = (params: {
  search: string;
  startFromOrganizationID?: string;
  sortBy?: AdminOrganizationListStreamSortBy;
  sortOrder?: SortOrder;
  filter?: OrganizationListFilter;
  subscriptionEndDateFrom?: Date;
  subscriptionEndDateTo?: Date;
  onlyActiveSubscriptions?: boolean;
}) => void;

export const useOrganizationListStream = () => {
  const dispatch = useAppDispatch();

  // Kept per hook instance: a module-level controller would let a second
  // consumer abort the first one's stream.
  const abortControllerRef = useRef<AbortController>();

  const openOrganizationListStream: OpenOrganizationListStream = useCallback(
    async ({
      search,
      startFromOrganizationID = '',
      sortBy,
      sortOrder,
      filter,
      subscriptionEndDateFrom,
      subscriptionEndDateTo,
      onlyActiveSubscriptions = false,
    }) => {
      const abortController = new AbortController();

      abortControllerRef.current = abortController;

      dispatch(organizationsModel.actions.setLoading(true));

      try {
        const organizationsListStream =
          api.core.admin.adminOrganizationListStream(
            {
              StartFromOrganizationID: startFromOrganizationID,
              SearchString: search,
              Limit: ORGANIZATION_LIST_PAGE_SIZE,
              Sort: [
                {
                  By: sortBy,
                  Ascending: sortOrder === 'ascend',
                },
              ],
              Filter: filter,
              SubscriptionEndDateFrom:
                subscriptionEndDateFrom &&
                Timestamp.fromDate(subscriptionEndDateFrom),
              SubscriptionEndDateTo:
                subscriptionEndDateTo &&
                Timestamp.fromDate(subscriptionEndDateTo),
              OnlyActiveSubscriptions: onlyActiveSubscriptions,
            },
            { signal: abortController.signal },
          );

        for await (const { Update } of organizationsListStream) {
          switch (Update.case) {
            case 'InitialOrganizations': {
              dispatch(
                organizationsModel.actions.addMany(Update.value.Organizations),
              );

              dispatch(organizationsModel.actions.setLoading(false));
              break;
            }
            case 'OrganizationUpdated': {
              dispatch(organizationsModel.actions.setOne(Update.value));
              break;
            }

            default: {
              // do nothing (Ping case)
            }
          }
        }
      } catch (err) {
        if (err instanceof ConnectError && err.code !== Code.Canceled) {
          dispatch(organizationsModel.actions.setLoading(false));

          console.error('AdminOrganizationListStream:: error: ', err);
        }
      }
    },
    [],
  );

  const closeOrganizationListStream = useCallback((clearStore = true) => {
    abortControllerRef.current?.abort();

    if (clearStore) {
      dispatch(organizationsModel.actions.removeAll());
    }
  }, []);

  return { openOrganizationListStream, closeOrganizationListStream };
};
